import { useTranslation } from 'react-i18next';
import type { Clinic } from '@/types/api';

/**
 * The clinic's letterhead on printed papers (offers, consents, the protocol):
 * logo, name, contacts, tax ids. `compact`: a small logo and everything on
 * one line ("Name | address | Tel. … | PIB …"), for the protocol, where every
 * millimetre of a page is another row.
 */
export function Letterhead({
  clinic,
  logoUrl,
  compact = false,
}: {
  clinic: Clinic | null;
  logoUrl: string | null;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const contacts = [
    clinic?.address,
    clinic?.phone ? `${t('offers.paper.phone')} ${clinic.phone}` : null,
    clinic?.email,
  ].filter(Boolean);
  const ids = [
    clinic?.pib ? `${t('offers.paper.pib')} ${clinic.pib}` : null,
    clinic?.mb ? `${t('offers.paper.mb')} ${clinic.mb}` : null,
  ].filter(Boolean);
  if (compact) {
    const line = [clinic?.name, ...contacts, ids.join(' · ')].filter(Boolean);
    return (
      <header className="offer-paper__head offer-paper__head--compact">
        {logoUrl && <img className="offer-paper__logo" src={logoUrl} alt="" />}
        <p className="offer-paper__clinic-line">
          {line.map((part, i) => (
            <span key={part}>
              {i > 0 && <span className="offer-paper__sep"> | </span>}
              {i === 0 ? <strong>{part}</strong> : part}
            </span>
          ))}
        </p>
      </header>
    );
  }
  return (
    <header className="offer-paper__head">
      {logoUrl ? (
        <img className="offer-paper__logo" src={logoUrl} alt="" />
      ) : (
        <span className="offer-paper__logo" />
      )}
      <div className="offer-paper__clinic">
        <strong>{clinic?.name}</strong>
        {contacts.map((line) => (
          <span key={line}>{line}</span>
        ))}
        {ids.length > 0 && <span>{ids.join(' · ')}</span>}
      </div>
    </header>
  );
}

/** "Made with Dentlog" at the foot of a printed paper. */
export function PaperFoot() {
  const { t } = useTranslation();
  return (
    <footer className="offer-paper__foot">
      <img src="/pwa-192x192.png" alt="" />
      <span>{t('offers.paper.madeWith')}</span>
    </footer>
  );
}
